import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, TextField, Typography, IconButton, Paper } from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import debounce from 'debounce';
import { textPattern } from '../../../constants/patterns';
import { FieldAliasRow } from '../types/fieldAliasTypes';


interface MandatoryFieldRowProps {
    row: FieldAliasRow;
    index: number;
    updateRow: (id: string, field: keyof FieldAliasRow, value: any) => void;
    undoDelete: (id: string) => void;
    isDuplicateAliasName: (aliasName: string, currentId: string) => boolean;
}

const MandatoryFieldRow: React.FC<MandatoryFieldRowProps> = ({
    row,
    index,
    updateRow,
    undoDelete,
    isDuplicateAliasName
}) => {
    // Local state for immediate UI updates
    const [localAliasName, setLocalAliasName] = useState(row.alias_name);
    
    // Sync external changes to local state
    useEffect(() => {
        setLocalAliasName(row.alias_name);
    }, [row.alias_name, row.is_active, row.field_alias_id]);
    
    // Create debounced update function with cleanup
    const debouncedUpdateRef = useRef<ReturnType<typeof debounce>>();
    
    const debouncedUpdate = useCallback((aliasName: string) => {
        updateRow(row.id, 'alias_name', aliasName);
    }, [updateRow, row.id]);
    
    useEffect(() => {
        debouncedUpdateRef.current = debounce(debouncedUpdate, 300);
        
        return () => {
            debouncedUpdateRef.current?.clear();
        };
    }, [debouncedUpdate]);
    
    // Handle alias name changes
    const handleAliasChange = (value: string) => {
        const cleanValue = value.replace(textPattern, "");
        setLocalAliasName(cleanValue);
        debouncedUpdateRef.current?.(cleanValue);
    };
    
    const isDuplicate = isDuplicateAliasName(localAliasName, row.id);
    const hasEmptyAlias = !localAliasName.trim();

    return (
        <Paper 
            elevation={1} 
            sx={{ 
                p: 1.5, 
                mb: 0.5, 
                opacity: !row.is_active ? 0.6 : 1,
                backgroundColor: !row.is_active ? '#f5f5f5' : '#e3f2fd',
                border: '2px solid #1976d2',
                position: 'relative'
            }}
        >
            {/* Deleted overlay */}
            {!row.is_active && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 60,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1,
                        borderRadius: 1,
                        pointerEvents: 'none'
                    }}
                >
                    <Typography variant="h6" color="error" sx={{ fontWeight: 'bold' }}>
                        MARKED FOR DELETION
                    </Typography>
                </Box>
            )}
            
            <Box display="flex" alignItems="center" gap={2}>
                <Box display="flex" gap={2} flex={1} mr={8}>
                    <Box width="20%">
                        <Typography variant="subtitle1" sx={{ 
                            color: '#1976d2',
                            fontWeight: 'bold'
                        }}>
                            {row.isSubPrimary ? '🔐 SUB_PRIMARY Field' : '🔒 Mandatory Field'}
                        </Typography>
                        <Typography variant="caption" sx={{ 
                            fontSize: '0.7rem',
                            color: '#1976d2'
                        }}>
                            {row.isSubPrimary ? 'Alias Name Locked' : 'Required for Upload'}
                        </Typography>
                    </Box>
                    
                    <Box width="40%">
                        <TextField
                            label="Alias Name"
                            value={localAliasName}
                            onChange={(e) => handleAliasChange(e.target.value)}
                            fullWidth
                            size="small"
                            required
                            disabled={!row.is_active || row.isSubPrimary}
                            error={isDuplicate || hasEmptyAlias}
                            helperText={
                                row.isSubPrimary ? 'SUB_PRIMARY field alias is locked and cannot be edited' :
                                hasEmptyAlias ? 'Alias name is required' :
                                isDuplicate ? 'Duplicate alias name detected' : ''
                            }
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&.Mui-error': {
                                        '& fieldset': {
                                            borderColor: '#f44336',
                                        },
                                    },
                                },
                            }}
                            slotProps={{
                                htmlInput: {
                                    maxLength: 100,
                                },
                            }}
                        />
                    </Box>
                    
                    <Box width="40%">
                        <TextField
                            label="Map to field"
                            value={`✓ Maps to: ${row.field_name}`}
                            fullWidth
                            size="small"
                            disabled
                            sx={{
                                '& .MuiInputBase-input': {
                                    color: '#1976d2',
                                    fontWeight: 'bold'
                                }
                            }}
                        />
                    </Box>
                </Box>
                
                <Box display="flex" gap={1} sx={{ zIndex: 2, position: 'relative' }}>
                    {!row.is_active && (
                        <IconButton
                            size="small"
                            onClick={() => undoDelete(row.id)}
                            color="primary"
                            title="Undo Delete"
                            sx={{ backgroundColor: 'white', '&:hover': { backgroundColor: '#f0f0f0' } }}
                        >
                            <UndoIcon />
                        </IconButton>
                    )}
                </Box>
            </Box>
        </Paper>
    );
};

export default MandatoryFieldRow;